import { queryClient } from '@/app/providers/react-query-provider';

import { apiDealFetch } from '..';
import { DEAL_QUERY_KEYS } from './queries';
import type {
  AdminDeleteOfferPayload,
  AdminUpdateOfferPayload,
  CreateCompanyPayload,
  CreateInquiryPayload,
  CreateOfferPayload,
  FillOfferPayload,
  FillOfferRes,
  IdRes,
  OfferConfidenceScoreType,
  UpdateCompanyPayload,
} from './types';

// ==================== Company Mutations ====================

export const _createCompany = (body: CreateCompanyPayload) => {
  return apiDealFetch<IdRes>('/companies', {
    method: 'POST',
    body,
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companies] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.entityCompanies] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.adminCompanies] });
    },
  });
};

export const _updateCompany = ({
  companyId,
  body,
}: {
  companyId: string;
  body: UpdateCompanyPayload;
}) => {
  return apiDealFetch<void>(`/companies/${companyId}`, {
    method: 'PUT',
    body,
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companies] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companyById, companyId] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companySummary, companyId] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.entityCompanies] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.adminCompanies] });
    },
  });
};

export const _deleteCompany = (companyId: string) => {
  return apiDealFetch<void>(`/companies/${companyId}`, {
    method: 'DELETE',
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companies] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.entityCompanies] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.adminCompanies] });
    },
  });
};

// ==================== Following Mutations ====================

export const _followCompany = (companyId: string) => {
  return apiDealFetch<void>(`/companies/${companyId}/following`, {
    method: 'POST',
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companies] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companyById, companyId] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companySummary, companyId] });
    },
  });
};

export const _unfollowCompany = (companyId: string) => {
  return apiDealFetch<void>(`/companies/${companyId}/following`, {
    method: 'DELETE',
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companies] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companyById, companyId] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companySummary, companyId] });
    },
  });
};

// ==================== Offer Mutations ====================

const invalidateOfferMarketDepth = () =>
  queryClient.invalidateQueries({
    queryKey: [DEAL_QUERY_KEYS.marketDepth],
    refetchType: 'all',
  });

export const _createOffer = ({
  companyId,
  body,
}: {
  companyId: string;
  body: CreateOfferPayload;
}) => {
  return apiDealFetch<IdRes>(`/companies/${companyId}/offers`, {
    method: 'POST',
    body,
    onResponse: ({ response }) => {
      if (response.ok) void invalidateOfferMarketDepth();
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companyOffers, companyId] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companyById, companyId] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.entityOffers] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.entityDashboard] });
    },
  });
};

export const _updateOffer = ({
  companyId,
  offerId,
  body,
}: {
  companyId: string;
  offerId: string;
  body: CreateOfferPayload;
}) => {
  return apiDealFetch<void>(`/companies/${companyId}/offers/${offerId}`, {
    method: 'PUT',
    body,
    onResponse: ({ response }) => {
      if (response.ok) void invalidateOfferMarketDepth();
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companyOffers, companyId] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.offerById, companyId, offerId] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companyById, companyId] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.entityOffers] });
    },
  });
};

export const _deleteOffer = ({ companyId, offerId }: { companyId: string; offerId: string }) => {
  return apiDealFetch<void>(`/companies/${companyId}/offers/${offerId}`, {
    method: 'DELETE',
    onResponse: ({ response }) => {
      if (response.ok) void invalidateOfferMarketDepth();
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companyOffers, companyId] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companyById, companyId] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.entityOffers] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.entityDashboard] });
    },
  });
};

export const _submitOffer = ({ companyId, offerId }: { companyId: string; offerId: string }) => {
  return apiDealFetch<void>(`/companies/${companyId}/offers/${offerId}/submit`, {
    method: 'POST',
    onResponse: ({ response }) => {
      if (response.ok) void invalidateOfferMarketDepth();
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companyOffers, companyId] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.offerById, companyId, offerId] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companyById, companyId] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.entityOffers] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.entityDashboard] });
    },
  });
};

export const _closeOffer = ({ companyId, offerId }: { companyId: string; offerId: string }) => {
  return apiDealFetch<void>(`/companies/${companyId}/offers/${offerId}/close`, {
    method: 'POST',
    onResponse: ({ response }) => {
      if (response.ok) void invalidateOfferMarketDepth();
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companyOffers, companyId] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.offerById, companyId, offerId] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companyById, companyId] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.entityOffers] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.entityDashboard] });
    },
  });
};

export const _fillOffer = ({
  companyId,
  offerId,
  body,
}: {
  companyId: string;
  offerId: string;
  body: FillOfferPayload;
}) => {
  return apiDealFetch<FillOfferRes>(`/companies/${companyId}/offers/${offerId}/fill`, {
    method: 'POST',
    body,
    onResponse: ({ response }) => {
      if (response.ok) void invalidateOfferMarketDepth();
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companyOffers, companyId] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.offerById, companyId, offerId] });
      queryClient.invalidateQueries({
        queryKey: [DEAL_QUERY_KEYS.offerFillHistory, companyId, offerId],
      });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companyById, companyId] });
    },
  });
};

export const _adminUpdateOffer = ({
  offerId,
  companyId,
  body,
}: {
  offerId: string;
  companyId?: string;
  body: AdminUpdateOfferPayload;
}) => {
  return apiDealFetch<void>(`/admin/offers/${offerId}`, {
    method: 'PUT',
    body,
    onResponse: ({ response }) => {
      if (response.ok) void invalidateOfferMarketDepth();
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companyOffers] });
      if (companyId) {
        queryClient.invalidateQueries({
          queryKey: [DEAL_QUERY_KEYS.offerById, companyId, offerId],
        });
        queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companyById, companyId] });
        queryClient.invalidateQueries({
          queryKey: [DEAL_QUERY_KEYS.companySummary, companyId],
        });
      }
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.entityOffers] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.entityDashboard] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.adminCompanies] });
    },
  });
};

export const _setOfferConfidenceScore = ({
  offerId,
  confidenceScore,
}: {
  offerId: string;
  confidenceScore: OfferConfidenceScoreType;
}) => {
  return apiDealFetch<void>(`/admin/offers/${offerId}/confidence-score`, {
    method: 'PATCH',
    body: { confidenceScore },
    onResponse: ({ response }) => {
      if (response.ok) void invalidateOfferMarketDepth();
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.adminOffers] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companyOffers] });
    },
  });
};

export const _adminDeleteOffer = ({
  offerId,
  companyId,
  body,
}: {
  offerId: string;
  companyId?: string;
  body?: AdminDeleteOfferPayload;
}) => {
  return apiDealFetch<void>(`/admin/offers/${offerId}`, {
    method: 'DELETE',
    body: body ?? {},
    onResponse: ({ response }) => {
      if (response.ok) void invalidateOfferMarketDepth();
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companyOffers] });
      if (companyId) {
        queryClient.invalidateQueries({
          queryKey: [DEAL_QUERY_KEYS.offerById, companyId, offerId],
        });
        queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.companyById, companyId] });
        queryClient.invalidateQueries({
          queryKey: [DEAL_QUERY_KEYS.companySummary, companyId],
        });
      }
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.entityOffers] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.entityDashboard] });
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.adminCompanies] });
    },
  });
};

export const _inquireOffer = ({
  companyId,
  offerId,
  body,
}: {
  companyId: string;
  offerId: string;
  body: CreateInquiryPayload;
}) => {
  return apiDealFetch<IdRes>(`/companies/${companyId}/offers/${offerId}/inquire`, {
    method: 'POST',
    body,
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [DEAL_QUERY_KEYS.entityDashboard] });
    },
  });
};
